// Reviews management page component
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColorModeValue } from '../hooks/useColorModeValue';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Card,
  Text,
  IconButton,
  Spinner,
  Grid,
  Input,
  Flex,
  Icon,
  Badge,
  Progress,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
  SelectContent,
  SelectItem,
  createListCollection,
  Tooltip,
  StatRoot,
  StatLabel,
  StatValueText,
  CollapsibleRoot,
  CollapsibleContent,
  Table,
  Alert,
} from '@chakra-ui/react';
import {
  FiTrash2,
  FiSearch,
  FiCheckCircle,
  FiDownload,
  FiChevronDown,
  FiChevronRight,
  FiEye,
} from 'react-icons/fi';
import {
  useReviews,
  useDeleteReview,
  useDownloadReviewReport,
  useReviewStatus,
} from '../hooks/useApi';
import { PolicyReview } from '../types';
import { formatDate, formatCompactDateTime } from '../utils/dateUtils';
import { toaster } from '../components/ui/toaster';

const ReviewsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const { data: reviews, isLoading, error } = useReviews();
  const deleteMutation = useDeleteReview();
  const downloadMutation = useDownloadReviewReport();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const statusOptions = useMemo(() => [
    { label: t('reviews.allStatus'), value: 'all' },
    { label: t('reviews.completed'), value: 'completed' },
    { label: t('reviews.processing'), value: 'processing' },
    { label: t('reviews.pending'), value: 'pending' },
    { label: t('reviews.failed'), value: 'failed' },
  ], [t]);

  // Filter and sort reviews
  const filteredAndSortedReviews = useMemo(() => {
    if (!reviews) return [];

    let filtered = reviews.filter((review) => {
      const matchesSearch = 
        review.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.policy?.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.standard?.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.system_standard?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || review.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Sort: completed first, then by creation date (newest first)
    return filtered.sort((a, b) => {
      // Completed reviews first
      if (a.status === 'completed' && b.status !== 'completed') return -1;
      if (a.status !== 'completed' && b.status === 'completed') return 1;
      
      // Then by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [reviews, searchTerm, statusFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!reviews) return { total: 0, completed: 0, processing: 0, failed: 0 };
    
    return {
      total: reviews.length,
      completed: reviews.filter(r => r.status === 'completed').length,
      processing: reviews.filter(r => r.status === 'processing').length,
      failed: reviews.filter(r => r.status === 'failed').length,
    };
  }, [reviews]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  const getStatusColorPalette = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'processing': return 'blue';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const toggleRowExpansion = (reviewId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedRows(newExpanded);
  };

  const handleDelete = async (reviewId: number) => {
    if (window.confirm(t('reviews.confirmDeleteAssessment'))) {
      try {
        await deleteMutation.mutateAsync(reviewId.toString());
        toaster.create({
          title: t('reviews.assessmentDeletedSuccessfully'),
          type: 'success',
        });
      } catch (error: any) {
        toaster.create({
          title: t('reviews.deleteFailed'),
          description: error.response?.data?.message || t('reviews.errorOccurred'),
          type: 'error',
        });
      }
    }
  };

  const handleDownload = async (review: PolicyReview) => {
    try {
      const blob = await downloadMutation.mutateAsync(review.id.toString());
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const policyName = review.policy?.name?.replace(/[^a-zA-Z0-9]/g, '_') || t('reviews.policyFilenameFallback');
      const standardName = review.standard?.name?.replace(/[^a-zA-Z0-9]/g, '_') || t('reviews.standardFilenameFallback');
      link.download = `Neurev_${t('reviews.assessmentFilenamePart')}_${policyName}_${standardName}_${review.id}.docx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('reviews.reportDownloadedSuccessfully'),
        type: 'success',
      });
    } catch (error: any) {
      toaster.create({
        title: t('reviews.downloadFailed'),
        description: error.response?.data?.message || t('reviews.errorOccurred'),
        type: 'error',
      });
    }
  };

  const ReviewRow: React.FC<{ review: PolicyReview }> = ({ review }) => {
    const { t } = useTranslation();

    const getStatusLabel = (status?: string) => {
      switch (status) {
        case 'completed': return t('reviews.completed');
        case 'processing': return t('reviews.processing');
        case 'pending': return t('reviews.pending');
        case 'failed': return t('reviews.failed');
        default: return t('reviews.unknownStatus');
      }
    };

    const getComplianceLevel = (score: number) => {
      if (score >= 80) return t('reviews.excellent');
      if (score >= 60) return t('reviews.good');
      if (score >= 40) return t('reviews.fair');
      return t('reviews.poor');
    };

    // Use real-time status tracking for processing reviews
    const isProcessing = review.status === 'processing' || review.status === 'pending';
    const { data: reviewStatus } = useReviewStatus(
      isProcessing ? review.id.toString() : null,
      { 
        enabled: isProcessing,
        refetchInterval: 10000
      }
    );

    // Use real-time data if available, otherwise use the review data
    const currentReview = reviewStatus || review;
    const progressPercentage = reviewStatus?.progress_percentage || 0;
    const isExpanded = expandedRows.has(review.id);

    // Calculate sections info
    const getSectionsInfo = () => {
      if (currentReview.status === 'completed' && 'results' in currentReview && currentReview.results?.section_results) {
        const processed = currentReview.results.section_results.length;
        const total = 'system_standard' in currentReview && currentReview.system_standard?.sections_data?.sections?.length || 0;
        return `${processed}/${total}`;
      }
      return '-';
    };

    return (
      <>
        <Table.Row key={review.id} _hover={{ bg: 'gray.50' }}>
          <Table.Cell>
            <HStack gap={2}>
              <IconButton
                aria-label={t('reviews.expandRow')}
                size="sm"
                variant="ghost"
                onClick={() => toggleRowExpansion(review.id)}
              >
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              </IconButton>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" fontWeight="semibold" color="blue.500">
                  {review.name || t('reviews.assessmentNumber', { id: review.id })}
                </Text>
                {review.description && (
                  <Text fontSize="xs" color="gray.600" lineClamp={1}>
                    {review.description}
                  </Text>
                )}
              </VStack>
            </HStack>
          </Table.Cell>
          <Table.Cell>
            <Badge colorPalette={getStatusColorPalette(currentReview.status)} size="sm">
              {getStatusLabel(currentReview.status)}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            {currentReview.status === 'completed' ? (
              <HStack gap={2}>
                <Text fontSize="sm" fontWeight="semibold" color={`${getScoreColor(currentReview.compliance_score || 0)}.500`}>
                  {currentReview.compliance_score || 0}%
                </Text>
                <Progress.Root
                  value={currentReview.compliance_score || 0}
                  colorPalette={getScoreColor(currentReview.compliance_score || 0)}
                  size="sm"
                  w="40px"
                  borderRadius="full"
                >
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
              </HStack>
            ) : (
              <Text fontSize="sm" color="gray.500">-</Text>
            )}
          </Table.Cell>
          <Table.Cell>
            <Text fontSize="sm" lineClamp={2}>
              {review.policy?.tag || review.policy?.name || t('reviews.unknownDocument')}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text fontSize="sm" lineClamp={2}>
              {review.system_standard?.name || review.standard?.name || review.standard?.tag || t('reviews.unknownStandard')}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text fontSize="sm">
              {formatDate(review.created_at)}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <HStack gap={1}>
              {currentReview.status === 'completed' && (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <IconButton
                      aria-label={t('reviews.download')}
                      size="sm"
                      variant="ghost"
                      colorPalette="blue"
                      onClick={() => handleDownload(review)}
                      loading={downloadMutation.isLoading}
                    >
                      <FiDownload />
                    </IconButton>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>{t('reviews.downloadReport')}</Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              )}
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <IconButton
                    aria-label={t('reviews.view')}
                    size="sm"
                    variant="ghost"
                    colorPalette="gray"
                    onClick={() => toggleRowExpansion(review.id)}
                  >
                    <FiEye />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>{t('reviews.viewDetails')}</Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <IconButton
                    aria-label={t('reviews.deleteAssessment')}
                    size="sm"
                    variant="ghost"
                    colorPalette="red"
                    onClick={() => handleDelete(review.id)}
                    loading={deleteMutation.isLoading}
                  >
                    <FiTrash2 />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>{t('reviews.deleteAssessment')}</Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            </HStack>
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell colSpan={7} p={0}>
            <CollapsibleRoot open={isExpanded}>
              <CollapsibleContent>
              <Box bg="gray.50" p={3} borderTop="1px" borderColor="gray.200">
                <VStack align="stretch" gap={2}>
                  {/* Description and Sections in one row */}
                  <HStack justify="space-between" align="start">
                    {review.description && (
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                          {t('reviews.description')}: <Text as="span" fontWeight="normal">{review.description}</Text>
                        </Text>
                      </Box>
                    )}
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {t('reviews.sections')}: <Text as="span" fontWeight="normal">{t('reviews.processedSections', { value: getSectionsInfo() })}</Text>
                      </Text>
                    </Box>
                  </HStack>

                  {/* Processing Progress */}
                  {currentReview.status === 'processing' && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                        {t('reviews.processing')}: <Text as="span" fontWeight="normal">
                          {Math.round(progressPercentage)}% ({t('reviews.sectionsCount', { completed: reviewStatus?.completed_sections || 0, total: reviewStatus?.total_sections || 0 })})
                        </Text>
                      </Text>
                      <Progress.Root
                        value={progressPercentage}
                        colorPalette="blue"
                        size="sm"
                        borderRadius="md"
                      >
                        <Progress.Track>
                          <Progress.Range />
                        </Progress.Track>
                      </Progress.Root>
                    </Box>
                  )}

                  {/* Additional Info in compact format */}
                  <HStack justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Text fontSize="sm" color="gray.700">
                      <strong>{t('reviews.id')}:</strong> {review.id}
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      <strong>{t('reviews.created')}:</strong> {formatCompactDateTime(review.created_at)}
                    </Text>
                    {currentReview.completed_at && (
                      <Text fontSize="sm" color="gray.700">
                        <strong>{t('reviews.completed')}:</strong> {formatCompactDateTime(currentReview.completed_at)}
                      </Text>
                    )}
                    {currentReview.status === 'completed' && currentReview.compliance_score !== null && currentReview.compliance_score !== undefined && (
                      <Text fontSize="sm" color="gray.700">
                        <strong>{t('reviews.level')}:</strong> {getComplianceLevel(currentReview.compliance_score)}
                      </Text>
                    )}
                  </HStack>
                </VStack>
              </Box>
              </CollapsibleContent>
            </CollapsibleRoot>
          </Table.Cell>
        </Table.Row>
      </>
    );
  };

  if (isLoading) {
    return (
      <Box bg={bg} minH="100vh" py={8}>
        <Container maxW="7xl">
          <VStack justify="center" align="center" h="60vh">
            <Spinner size="xl" color="brand.500" />
            <Text>{t('reviews.loadingAssessments')}</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bg} minH="100vh" py={8}>
        <Container maxW="7xl">
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Title>{t('reviews.failedToLoadAssessments')}</Alert.Title>
          </Alert.Root>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bg} minH="100vh" py={8}>
      <Container maxW="7xl">
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <VStack align="start" gap={1}>
              <Heading size="lg">{t('reviews.assessments')}</Heading>
              <Text color="gray.600">{t('reviews.manageTrackComplianceGapAssessments')}</Text>
            </VStack>
          </Flex>

          {/* Statistics */}
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
            <StatRoot>
              <StatLabel>{t('reviews.totalAssessments')}</StatLabel>
              <StatValueText>{stats.total}</StatValueText>
            </StatRoot>
            <StatRoot>
              <StatLabel>{t('reviews.completed')}</StatLabel>
              <StatValueText color="green.500">{stats.completed}</StatValueText>
            </StatRoot>
            <StatRoot>
              <StatLabel>{t('reviews.processing')}</StatLabel>
              <StatValueText color="blue.500">{stats.processing}</StatValueText>
            </StatRoot>
            <StatRoot>
              <StatLabel>{t('reviews.failed')}</StatLabel>
              <StatValueText color="red.500">{stats.failed}</StatValueText>
            </StatRoot>
          </Grid>

          {/* Search and Filters */}
          <HStack gap={4} wrap="wrap">
            <Box maxW="400px" position="relative">
              <Icon
                as={FiSearch}
                position="absolute"
                left="12px"
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
                zIndex={1}
                pointerEvents="none"
              />
              <Input
                placeholder={t('reviews.searchAssessmentsPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                pl="40px"
              />
            </Box>
            <SelectRoot
              collection={createListCollection({ items: statusOptions })}
              value={[statusFilter]}
              onValueChange={(e: any) => setStatusFilter(e.value[0])}
              maxW="200px"
            >
              <SelectTrigger>
                <SelectValueText placeholder={t('reviews.allStatus')} />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} item={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </HStack>

          {/* Reviews Table */}
          <Card.Root bg={cardBg} shadow="sm">
            <Card.Body p={0}>
              {filteredAndSortedReviews.length > 0 ? (
                <Box overflowX="auto">
                  <Table.Root variant="line" size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader fontSize="xs" w="25%">{t('reviews.assessment')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="10%">{t('reviews.status')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="12%">{t('reviews.score')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="20%">{t('reviews.document')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="20%">{t('reviews.standard')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="8%">{t('reviews.created')}</Table.ColumnHeader>
                        <Table.ColumnHeader fontSize="xs" w="5%">{t('reviews.actions')}</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {filteredAndSortedReviews.map((review) => (
                        <ReviewRow key={review.id} review={review} />
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              ) : (
                <VStack gap={4} py={8}>
                  <FiCheckCircle size="48px" color="gray.400" />
                  <VStack gap={2}>
                    <Text fontSize="lg" fontWeight="bold">
                      {t('reviews.noAssessmentsFound')}
                    </Text>
                    <Text color="gray.600" textAlign="center">
                      {searchTerm || statusFilter !== 'all'
                        ? t('reviews.noAssessmentsMatchCriteria')
                        : t('reviews.startFirstAssessment')}
                    </Text>
                  </VStack>
                  {!searchTerm && statusFilter === 'all' && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      {t('reviews.useNewAssessmentPage')}
                    </Text>
                  )}
                </VStack>
              )}
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default ReviewsPage;
